import { Suspense, useEffect, useId, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  BadgeCheck,
  Bell,
  Building,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ChartLine,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileChartColumn,
  FileSpreadsheet,
  FileUp,
  GitCompareArrows,
  Handshake,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  ListTodo,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  RefreshCw,
  Scale,
  ScanSearch,
  ShieldCheck,
  Sun,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { operationalToday } from '@ana/domain';
import { api } from '../api/client.ts';
import { useSession } from '../auth/session.tsx';
import { MSG } from '../content/messages.ts';
import { useSyncSnapshot } from '../offline/sync-state.ts';
import { dateTimeBr } from '../lib/format.ts';
import { useTheme } from '../lib/theme.ts';
import { useIdle } from '../lib/idle.ts';
import { Loading } from './ui.tsx';
import { IMPORT_PERMISSIONS } from '../pages/imports/meta.ts';
import { ANALYTICS_SCREENS } from '../pages/analytics/screens.ts';
import { TourProvider } from '../tour/TourProvider.tsx';
import { TourOffer } from '../tour/TourOffer.tsx';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  perm?: string;
  /** Shown when the unit grants any of these (a screen shared by several profiles). */
  anyPerm?: readonly string[];
  /** A system permission: the server grants it through a role in any active unit, not only the selected one. */
  anyUnit?: boolean;
  /** Also shown when any unit grants this system permission (a screen with a reading for that profile). */
  orAnyUnit?: string;
  /** Anchor of the guided tour (tour/catalog.ts). */
  tour?: string;
}

/**
 * The pending-items centre answers for a unit when the profile reads at least one of its sources (canReadCentre in
 * apps/api/src/modules/tasks/projection.ts): exceptions, alerts, review queue, movement, privileged requests, import
 * batches or manual tasks. Kept in step with that list, so the menu never offers a screen the API refuses.
 */
const TASK_CENTRE_PERMISSIONS: readonly string[] = [
  ...new Set([
    'exception.read',
    'alert.read',
    'finance.read',
    'movement.read',
    'user.approve_privileged',
    'user.manage',
    'task.manage',
    ...IMPORT_PERMISSIONS,
  ]),
];

/**
 * The analysis index opens when at least one of its screens does: report.read (anomalies, forecast status, simulator,
 * adjusted comparatives), rules.manage (operating calendar) and the assistant's sources. Read from the screens' own
 * list, so the menu cannot drift from what each analytics screen accepts.
 */
const ANALYTICS_PERMISSIONS: readonly string[] = [...new Set(ANALYTICS_SCREENS.flatMap((s) => s.anyOf))];

export const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Operação',
    items: [
      // ADMIN has no dashboard.read but opens the Saúde do Sistema perspective of the panel (allowedPerspectives).
      {
        to: '/',
        label: 'Painel executivo',
        icon: LayoutDashboard,
        perm: 'dashboard.read',
        orAnyUnit: 'system.health.read',
      },
      { to: '/movimento', label: 'Movimento diário', icon: CalendarDays, perm: 'movement.read' },
      { to: '/aprovacoes', label: 'Aprovações', icon: ClipboardCheck, perm: 'movement.read' },
      { to: '/excecoes', label: 'Divergências', icon: TriangleAlert, perm: 'exception.read' },
      { to: '/alertas', label: 'Alertas', icon: Bell, perm: 'alert.read' },
      { to: '/pendencias', label: 'Pendências e tarefas', icon: ListTodo, anyPerm: TASK_CENTRE_PERMISSIONS },
      { to: '/sincronizacao', label: 'Sincronização', icon: RefreshCw },
    ],
  },
  {
    group: 'Financeiro',
    items: [
      { to: '/conciliacao', label: 'Conciliação', icon: Scale, perm: 'finance.read' },
      {
        to: '/conciliacao/assistida',
        label: 'Conciliação assistida',
        icon: ScanSearch,
        perm: 'finance.read',
      },
      { to: '/extratos', label: 'Extratos', icon: FileSpreadsheet, perm: 'finance.read' },
      { to: '/depositos', label: 'Depósitos', icon: Landmark, perm: 'finance.read' },
      { to: '/despesas', label: 'Despesas', icon: Receipt, perm: 'finance.read' },
      { to: '/importacao', label: 'Importação de dados', icon: FileUp, anyPerm: IMPORT_PERMISSIONS },
    ],
  },
  {
    group: 'Gestão',
    items: [
      { to: '/relatorios/receitas', label: 'Receitas', icon: TrendingUp, perm: 'report.read' },
      { to: '/relatorios/diario', label: 'Resultado diário', icon: CalendarCheck, perm: 'report.read' },
      { to: '/relatorios/semanal', label: 'Resultado semanal', icon: CalendarRange, perm: 'report.read' },
      { to: '/relatorios/mensal', label: 'Resultado mensal', icon: ChartColumn, perm: 'report.read' },
      { to: '/relatorios/comparativos', label: 'Comparativos', icon: GitCompareArrows, perm: 'report.read' },
      {
        to: '/relatorios/conciliacao',
        label: 'Relatório de conciliação',
        icon: ListChecks,
        perm: 'report.read',
      },
      {
        to: '/relatorios/excecoes',
        label: 'Relatório de exceções',
        icon: ClipboardList,
        perm: 'report.read',
      },
      {
        to: '/relatorios/convenios',
        label: 'Mensalistas e convênios',
        icon: Handshake,
        perm: 'report.read',
      },
      { to: '/relatorios/dre', label: 'DRE gerencial', icon: FileChartColumn, perm: 'report.read' },
      // The quality indicators are read with dashboard.read (GET /v1/quality/indicators).
      { to: '/qualidade', label: 'Qualidade dos dados', icon: BadgeCheck, perm: 'dashboard.read' },
      { to: '/vazamento', label: 'Perda de receita', icon: TrendingDown, perm: 'report.read' },
      { to: '/analises', label: 'Análises', icon: ChartLine, anyPerm: ANALYTICS_PERMISSIONS },
      { to: '/auditoria', label: 'Auditoria', icon: ShieldCheck, perm: 'audit.read' },
    ],
  },
  {
    group: 'Administração',
    items: [
      { to: '/cadastros', label: 'Cadastros', icon: Database },
      { to: '/admin', label: 'Usuários e perfis', icon: Users, perm: 'user.manage' },
      // D-31 (PROPOSTA): ADMIN and GESTOR read, only ADMIN acts; the server checks it on every /v1/ops route.
      {
        to: '/saude-do-sistema',
        label: 'Saúde do Sistema',
        icon: Activity,
        perm: 'system.health.read',
        anyUnit: true,
      },
    ],
  },
  {
    // Static content and the guided tours (TOUR-01): open to every profile, offline included.
    group: 'Suporte',
    items: [{ to: '/ajuda', label: 'Ajuda', icon: LifeBuoy, tour: 'menu-ajuda' }],
  },
];

/**
 * Items whose address starts another item's (`/` and `/conciliacao` → `/conciliacao/assistida`): they are marked
 * current only on their exact address, so a single menu item is highlighted at a time.
 */
const NAV_PARENTS = new Set(
  NAV.flatMap((g) => g.items.map((i) => i.to)).filter(
    (to, _, all) => to === '/' || all.some((o) => o.startsWith(`${to}/`)),
  ),
);

/**
 * Menu groups and items a unit's permissions open. Only a convenience: every screen also checks on its own, and the
 * server refuses the data. Offline, the cached identity keeps the daily movement reachable. `anyUnitPermissions`
 * holds what any of the person's units grants, for the system items (requireSystem in ops/routes.ts).
 */
export function visibleNav(
  permissions: readonly string[],
  offlineIdentity = false,
  anyUnitPermissions: readonly string[] = permissions,
) {
  const has = (p: string) => permissions.includes(p);
  const canSee = (perm?: string) => !perm || has(perm) || (perm === 'movement.read' && offlineIdentity);
  const canCatalog = ['catalog.manage', 'rules.manage', 'movement.read'].some(has);
  return NAV.map((g) => ({
    group: g.group,
    items: g.items.filter((i) =>
      i.to === '/cadastros'
        ? canCatalog
        : i.anyPerm
          ? i.anyPerm.some(has)
          : Boolean(i.anyUnit && i.perm && anyUnitPermissions.includes(i.perm)) ||
            Boolean(i.orAnyUnit && anyUnitPermissions.includes(i.orAnyUnit)) ||
            canSee(i.perm),
    ),
  })).filter((g) => g.items.length > 0);
}

/** Group and item of the current route (longest matching prefix), for the breadcrumb. */
export function navContext(pathname: string): { group: string; label: string } | null {
  let best: { group: string; label: string; len: number } | null = null;
  for (const g of NAV) {
    for (const i of g.items) {
      const match = i.to === '/' ? pathname === '/' : pathname === i.to || pathname.startsWith(`${i.to}/`);
      if (match && (!best || i.to.length > best.len))
        best = { group: g.group, label: i.label, len: i.to.length };
    }
  }
  return best ? { group: best.group, label: best.label } : null;
}

const SYNC_LABEL: Record<string, { text: string; tone: string }> = {
  ONLINE: { text: 'Conectado', tone: 'ok' },
  OFFLINE: { text: 'Sem conexão', tone: 'warn' },
  PENDING_SYNC: { text: 'Pendente de sincronização', tone: 'warn' },
  SYNCING: { text: 'Sincronizando', tone: 'info' },
  SYNCED: { text: 'Sincronizado', tone: 'ok' },
  CONFLICT: { text: 'Conflito', tone: 'danger' },
  ERROR: { text: 'Erro de sincronização', tone: 'danger' },
};

export function SyncPill() {
  const snapshot = useSyncSnapshot();
  const s = SYNC_LABEL[snapshot.state] ?? SYNC_LABEL.ONLINE!;
  return (
    <NavLink
      to="/sincronizacao"
      className={`badge ${s.tone}`}
      data-testid="sync-status"
      data-tour="sincronizacao"
      title={`Última confirmação do servidor: ${dateTimeBr(snapshot.lastServerAck)}`}
    >
      {s.text}
      {snapshot.pending > 0 ? ` · ${snapshot.pending} pendente(s)` : ''}
      {snapshot.conflicts > 0 ? ` · ${snapshot.conflicts} conflito(s)` : ''}
    </NavLink>
  );
}

const COLLAPSE_KEY = 'ana.sidebar.collapsed';
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: 'UTC' });
function todayLabel(): string {
  const iso = operationalToday(new Date());
  const [y, m, d] = iso.split('-');
  return `${weekday.format(new Date(`${iso}T12:00:00Z`)).replace('.', '')}, ${d}/${m}/${y}`;
}

function AlertsBell({ unitId }: { unitId: string }) {
  // The counter waits for the first screen (idle after load): one request less competing with it on a phone.
  const idle = useIdle();
  const q = useQuery({
    enabled: idle,
    queryKey: ['alerts-open-count', unitId],
    queryFn: () => api<unknown[]>(`/alerts?unitId=${unitId}&status=UNRESOLVED`),
    refetchInterval: 120_000,
  });
  const n = q.data?.length ?? 0;
  return (
    <NavLink
      to="/alertas"
      className="icon-btn"
      aria-label={`Alertas não resolvidos: ${n}`}
      title="Alertas não resolvidos"
    >
      <Bell size={20} aria-hidden="true" />
      {n > 0 ? (
        <span className="count" aria-hidden="true">
          {n > 99 ? '99+' : n}
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * Light/dark switch. The name says what a press does; the description carries the current state, so a screen reader
 * hears both ("Ativar modo escuro — Tema atual: claro").
 */
function ThemeToggle() {
  const { theme, preference, toggle } = useTheme();
  const stateId = useId();
  const dark = theme === 'dark';
  const action = dark ? 'Ativar modo claro' : 'Ativar modo escuro';
  return (
    <>
      <button
        type="button"
        className="icon-btn theme-toggle"
        aria-label={action}
        title={action}
        aria-describedby={stateId}
        data-testid="theme-toggle"
        data-tour="tema"
        onClick={toggle}
      >
        {dark ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
      </button>
      <span id={stateId} className="visually-hidden">
        {dark ? 'Tema atual: escuro' : 'Tema atual: claro'}
        {preference === 'system' ? ', conforme o sistema' : ''}
      </span>
    </>
  );
}

/**
 * Every `.table-wrap` may scroll horizontally on small screens: it must be reachable by keyboard and named
 * (WCAG 2.1.1 / axe scrollable-region-focusable). Applied centrally so no table can be forgotten, including
 * content rendered later; React does not manage these attributes on the wrappers.
 */
function useAccessibleScrollRegions() {
  useEffect(() => {
    const root = document.getElementById('conteudo');
    if (!root) return;
    const name = (el: HTMLElement): string => {
      const caption = el.querySelector('caption')?.textContent?.trim();
      if (caption) return caption;
      let prev = el.previousElementSibling;
      while (prev && !/^H[1-4]$/.test(prev.tagName)) prev = prev.previousElementSibling;
      const heading =
        prev?.textContent?.trim() ||
        el.closest('section, .card')?.querySelector('h2, h3')?.textContent?.trim();
      return heading ? `Tabela: ${heading}` : 'Tabela de dados';
    };
    const apply = () => {
      root.querySelectorAll<HTMLElement>('.table-wrap:not([tabindex])').forEach((el) => {
        el.tabIndex = 0;
        el.setAttribute('role', 'region');
        el.setAttribute('aria-label', name(el));
      });
      // Phone card layout (styles.css): each cell carries its column header as a visible label.
      root.querySelectorAll<HTMLTableElement>('table.cards-mobile').forEach((table) => {
        const headers = [...table.querySelectorAll('thead th')].map((th) => th.textContent?.trim() ?? '');
        table.setAttribute('role', 'table');
        // Styles the region of a card table (styles.css) without :has(), which older mobile browsers lack.
        if (table.parentElement?.classList.contains('table-wrap'))
          table.parentElement.classList.add('cards-wrap');
        table.querySelectorAll('thead, tbody').forEach((g) => g.setAttribute('role', 'rowgroup'));
        table.querySelectorAll('tr').forEach((tr) => tr.setAttribute('role', 'row'));
        table.querySelectorAll('thead th').forEach((th) => th.setAttribute('role', 'columnheader'));
        table.querySelectorAll<HTMLElement>('tbody tr').forEach((tr) =>
          [...tr.children].forEach((td, i) => {
            td.setAttribute('role', 'cell');
            td.setAttribute('data-label', headers[i] ?? '');
          }),
        );
      });
    };
    apply();
    // Only childList is observed: setting attributes above does not retrigger the observer.
    const mo = new MutationObserver(apply);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
}

const initials = (name: string) =>
  name
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

export function Layout({ children }: { children?: ReactNode }) {
  const { session, unitId, setUnitId, logout, offlineIdentity } = useSession();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const location = useLocation();
  const unit = session?.units.find((u) => u.id === unitId);
  const canSee = (perm?: string) =>
    !perm || Boolean(unit?.permissions.includes(perm)) || (perm === 'movement.read' && offlineIdentity);
  const ctx = navContext(location.pathname);
  useAccessibleScrollRegions();

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage unavailable (private mode): the preference is simply not remembered */
    }
  }, [collapsed]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <TourProvider>
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>
      {session && session.environment !== 'PRODUCAO' ? (
        <div className="env-banner" role="note">
          <TriangleAlert size={14} aria-hidden="true" />
          <span>
            {MSG.environment.label}:{' '}
            <strong>
              {offlineIdentity
                ? MSG.environment.offline
                : (MSG.environment.names[session.environment] ?? session.environment)}
            </strong>{' '}
            — {MSG.environment.synthetic}
          </span>
        </div>
      ) : null}
      <div className={`app${collapsed ? ' collapsed' : ''}`}>
        <aside
          className={`sidebar ${open ? 'open' : ''}`}
          aria-label="Menu principal"
          id="menu-principal"
          data-tour="menu-principal"
        >
          {/* Lazy: on a phone the menu is closed (display: none) until opened, so the logo is not downloaded with
              the first screen; with the menu collapsed only the symbol is fetched. */}
          <div className="brand-block">
            <img
              className="logo"
              src="/brand/anashopping-logo-branco.png"
              alt="Anashopping"
              width={168}
              height={24}
              loading="lazy"
            />
            <img
              className="symbol"
              src="/brand/anashopping-simbolo-branco.png"
              alt="Anashopping"
              width={32}
              height={29}
              loading="lazy"
            />
            <span className="product">Estacionamento</span>
          </div>
          <nav className="nav" onClick={() => setOpen(false)}>
            {visibleNav(
              unit?.permissions ?? [],
              offlineIdentity,
              session?.units.flatMap((u) => u.permissions) ?? [],
            ).map(({ group, items }) => {
              return (
                <div key={group}>
                  <div className="group">{group}</div>
                  {items.map((i) => (
                    <NavLink
                      key={i.to}
                      to={i.to}
                      end={NAV_PARENTS.has(i.to)}
                      title={collapsed ? i.label : undefined}
                      data-tour={i.tour}
                    >
                      <i.icon size={18} aria-hidden="true" />
                      <span className="label">{i.label}</span>
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            Gestão do estacionamento ·{' '}
            {session ? (MSG.environment.names[session.environment] ?? session.environment) : ''} ·{' '}
            <NavLink to="/privacidade" onClick={() => setOpen(false)}>
              Aviso de privacidade
            </NavLink>
          </div>
        </aside>
        <button
          className={`sidebar-backdrop ${open ? 'open' : ''}`}
          aria-label="Fechar menu"
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        />
        <div className="content-col">
          <header className="app-header">
            <button
              className="icon-btn menu-toggle"
              aria-label="Abrir menu"
              aria-expanded={open}
              aria-controls="menu-principal"
              data-tour="menu-abrir"
              onClick={() => setOpen((o) => !o)}
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <button
              className="icon-btn collapse-toggle"
              aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              aria-pressed={collapsed}
              onClick={() => setCollapsed((c) => !c)}
            >
              {collapsed ? (
                <PanelLeftOpen size={20} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={20} aria-hidden="true" />
              )}
            </button>
            <nav className="crumbs" aria-label="Você está em" data-tour="trilha">
              {ctx ? (
                <>
                  <span className="hide-sm">{ctx.group}</span>
                  <ChevronRight size={14} aria-hidden="true" className="hide-sm" />
                  <strong aria-current="page">{ctx.label}</strong>
                </>
              ) : null}
            </nav>
            <span className="spacer" />
            <div className="meta">
              <span className="header-chip hide-sm" title="Data operacional (America/Sao_Paulo)">
                <CalendarDays size={16} aria-hidden="true" />
                {todayLabel()}
              </span>
              {session && session.units.length > 1 ? (
                <label className="header-chip">
                  <Building size={16} aria-hidden="true" />
                  <span className="visually-hidden">Unidade</span>
                  <select
                    value={unitId ?? ''}
                    onChange={(e) => setUnitId(e.target.value)}
                    aria-label="Unidade"
                  >
                    {session.units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.code} — {u.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : unit ? (
                <span className="header-chip hide-sm" title={unit.name}>
                  <Building size={16} aria-hidden="true" />
                  <strong>{unit.code}</strong>
                </span>
              ) : null}
              <SyncPill />
              {unitId && canSee('alert.read') && !offlineIdentity ? <AlertsBell unitId={unitId} /> : null}
              <ThemeToggle />
              <span className="user-chip" data-testid="current-user" data-tour="usuario">
                <span className="avatar" aria-hidden="true">
                  {initials(session?.user.displayName ?? '')}
                </span>
                <span className="who">
                  <strong>{session?.user.displayName}</strong>
                  <small>{unit ? unit.roles.join(', ') : ''}</small>
                  {session?.user.isDemo ? (
                    <small className="demo-account" data-testid="demo-account">
                      Conta de demonstração
                    </small>
                  ) : null}
                </span>
              </span>
              <button className="btn small ghost logout" title="Sair" onClick={() => void logout()}>
                <LogOut size={16} aria-hidden="true" />
                <span className="logout-label">Sair</span>
              </button>
            </div>
          </header>
          <main className="main" id="conteudo" tabIndex={-1} data-tour="conteudo">
            <TourOffer />
            {/* The header and the menu stay on screen while a screen's code downloads (slow mobile network):
                only the content area waits. */}
            <Suspense fallback={<Loading what="Carregando tela" />}>
              <div key={location.pathname}>{children ?? <Outlet />}</div>
            </Suspense>
          </main>
        </div>
      </div>
    </TourProvider>
  );
}
